import React from "react";
import {
  Container,
  Grid,
  Typography,
  Paper,
  Box,
  useTheme,
  alpha,
  useMediaQuery,
} from "@mui/material";
import {
  Store as RetailIcon,
  Inventory as WholesaleIcon,
  Build as ManufacturingIcon,
  Engineering as ServicesIcon,
  LocalShipping as DistributionIcon,
} from "@mui/icons-material";

const IndustrySection = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const industries = [
    {
      name: "Retail",
      description: "Point of sale integration and inventory management",
      icon: <RetailIcon sx={{ fontSize: 32 }} />,
      color: theme.palette.primary.main,
    },
    {
      name: "Wholesale",
      description: "Bulk order processing and distributor management",
      icon: <WholesaleIcon sx={{ fontSize: 32 }} />,
      color: theme.palette.info.main,
    },
    {
      name: "Manufacturing",
      description: "Production costing and supply chain optimization",
      icon: <ManufacturingIcon sx={{ fontSize: 32 }} />,
      color: theme.palette.success.main,
    },
    {
      name: "Services",
      description: "Project billing and resource allocation",
      icon: <ServicesIcon sx={{ fontSize: 32 }} />,
      color: theme.palette.warning.main,
    },
    {
      name: "Distribution",
      description: "Logistics management and route optimization",
      icon: <DistributionIcon sx={{ fontSize: 32 }} />,
      color: theme.palette.error.main,
    },
    {
      name: "Healthcare",
      description: "Patient billing and compliance management",
      icon: <RetailIcon sx={{ fontSize: 32 }} />,
      color: theme.palette.secondary.main,
    },
  ];

  return (
    <Box
      sx={{
        background: `linear-gradient(135deg, ${alpha(
          theme.palette.background.default,
          0.8
        )} 0%, ${alpha(theme.palette.background.paper, 0.9)} 100%)`,
        py: { xs: 2, md: 4 },
        position: "relative",
        overflow: "hidden",
        backgroundColor: "#d0e8ffff",
      }}
    >
      {/* Title */}
      <Box textAlign="center" mb={4}>
        <Typography
          variant="h3"
          sx={{
            fontWeight: 800,
            mb: 2,
            background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
            WebkitBackgroundClip: "text",
            WebkitTextFillColor: "transparent",
          }}
        >
          Built for your industry
        </Typography>
        <Typography
          variant="h6"
          color="text.secondary"
          sx={{ maxWidth: 600, mx: "auto", lineHeight: 1.6 }}
        >
          Specialized solutions tailored to your industry's unique accounting
          needs and compliance requirements.
        </Typography>
      </Box>
      {/* Marquee Wrapper */}
      <Box
        sx={{
          width: "100%",
          overflow: "hidden",
          position: "relative",
        }}
      >
        <Box
          sx={{
            display: "flex",
            gap: 3,
            width: "max-content",
            animation: "scrollMarquee 25s linear infinite",
            "@keyframes scrollMarquee": {
              "0%": { transform: "translateX(0)" },
              "100%": { transform: "translateX(-50%)" },
            },
          }}
        >
          {/* Items repeat twice for seamless loop */}
          {[...industries, ...industries].map((industry, index) => (
            <Paper
              key={index}
              elevation={0}
              sx={{
                p: 3,
                minWidth: 250,
                flex: "0 0 auto",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                textAlign: "center",
                borderRadius: 3,
                background: `linear-gradient(135deg, ${alpha(
                  theme.palette.background.paper,
                  0.8
                )} 0%, ${alpha(theme.palette.background.default, 0.6)} 100%)`,
                border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                transition: "all 0.3s ease-in-out",
                position: "relative",
                "&:hover": {
                  transform: "translateY(-5px)",
                  boxShadow: `0 16px 40px -10px ${alpha(industry.color, 0.2)}`,
                  borderColor: alpha(industry.color, 0.3),
                },
              }}
            >
              <Box
                sx={{
                  width: 70,
                  height: 70,
                  borderRadius: "50%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  mb: 2,
                  background: `linear-gradient(135deg, ${alpha(
                    industry.color,
                    0.1
                  )} 0%, transparent 100%)`,
                  color: alpha(theme.palette.text.primary, 0.7),
                  transition: "all 0.3s ease",
                }}
              >
                {industry.icon}
              </Box>
              <Typography variant="h6" fontWeight={700} mb={1}>
                {industry.name}
              </Typography>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ lineHeight: 1.5 }}
              >
                {industry.description}
              </Typography>
            </Paper>
          ))}
        </Box>
      </Box>
      {/* Call to action */}
      <Box textAlign="center" mt={4}>
        <Typography
          variant="body1"
          color="text.secondary"
          sx={{ mb: 1, fontStyle: "italic" }}
        >
          Don't see your industry?
        </Typography>
        <Typography
          variant="h6"
          sx={{
            fontWeight: 600,
            color: "primary.main",
            cursor: "pointer",
            "&:hover": {
              textDecoration: "underline",
            },
          }}
          onClick={() => {
            const adminNumber = "918000516051";
            const message =
              "Hello Team, I didn’t find my industry listed on your website. Can you help me with a custom solution?";
            const url = `https://wa.me/${adminNumber}?text=${encodeURIComponent(
              message
            )}`;

            window.open(url, "_blank");
          }}
        >
          Contact us for a custom solution →
        </Typography>
      </Box>
    </Box>
  );
};

export default IndustrySection;
